import {
  TransactionStatus,
  TransactionType,
} from '@commenda/api/new-sales-tax/transactions/types';
import {
  ActivityType,
  NexusStatus,
  SalesTaxTransactionsActivityType,
} from '@commenda/api/sales-tax/types';
import { Country, SalesTaxFilingFrequencyEnum } from '@prisma/client';
import { Country as CountryUtil, State } from 'country-state-city';

export const formatNumberWithCommasAndDecimals = (
  number: number,
  args?: { maximumFractionDigits?: number }
): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Math.min(2, args?.maximumFractionDigits ?? 2),
    maximumFractionDigits: args?.maximumFractionDigits ?? 2,
  }).format(number);
};

// Example: 34.67 -> 34.67, 34.60 -> 34.6, 34.00 -> 34
export const formatNumberWithCommasAndMinimalDecimals = (
  number: number,
  args?: { maximumFractionDigits?: number }
): string => {
  const formatted = formatNumberWithCommasAndDecimals(number, args);

  if (!formatted.includes('.')) {
    return formatted;
  }

  const [integerPart, fractionalPart] = formatted.split('.');
  const trimmedFractional = fractionalPart.replace(/0+$/, '');

  if (!trimmedFractional) {
    return integerPart;
  }

  return `${integerPart}.${trimmedFractional}`;
};

export const formatNumberWithCommas = (number: number): string => {
  return new Intl.NumberFormat('en-US').format(number);
};

export const getOverviewTitleColor = (type: ActivityType) => {
  switch (type) {
    case ActivityType.REMITTANCE:
      return 'text-text-success';
    case ActivityType.NEXUS_TRIGGERED:
      return 'text-text-error';
    case ActivityType.APPROACHING_NEXUS:
      return 'text-text-warning';
    default:
      return 'text-text-tertiary';
  }
};

export const calculateOverviewDataForTransactions = ({
  totalTransactions,
  totalTaxCollected,
  totalSales,
}: {
  totalTransactions: number;
  totalTaxCollected: number;
  totalSales: number;
}) => {
  return [
    {
      type: SalesTaxTransactionsActivityType.TOTAL_TRANSACTIONS,
      activityTitle: 'Total Transactions',
      value: formatNumberWithCommas(totalTransactions),
    },

    {
      type: SalesTaxTransactionsActivityType.TAX_COLLECTED,
      activityTitle: 'Tax Collected',
      value: formatNumberWithCommasAndDecimals(totalTaxCollected),
    },

    {
      type: SalesTaxTransactionsActivityType.TOTAL_SALES,
      activityTitle: 'Total',
      value: formatNumberWithCommasAndDecimals(totalSales),
    },
  ];
};

export const getTransactionOverviewStatsColor = (
  type: SalesTaxTransactionsActivityType
) => {
  switch (type) {
    default:
      return 'text-text-tertiary';
  }
};

export const getNexusStatusBadgeColor = (status: NexusStatus) => {
  switch (status) {
    case NexusStatus.Remittance:
      return 'success';
    case NexusStatus.NexusTriggered:
      return 'error';
    case NexusStatus.ApproachingNexus:
      return 'warning';
    case NexusStatus.NoNexus:
      return 'gray';
    default:
      return 'gray';
  }
};

export const getNexusStatusColor = (status: NexusStatus) => {
  switch (status) {
    case NexusStatus.Remittance:
      return 'text-utility-success-500';
    case NexusStatus.NexusTriggered:
      return 'text-utility-error-500';
    case NexusStatus.ApproachingNexus:
      return 'text-utility-warning-500';
    case NexusStatus.NoNexus:
      return 'text-utility-gray-500';
    default:
      return 'text-utility-gray-500';
  }
};

export const getNexusMapClasses = (status: NexusStatus) => {
  switch (status) {
    case NexusStatus.Remittance:
      return {
        stroke: 'stroke-utility-success-600',
        fill: 'fill-utility-success-300',
        background: 'bg-bg-success',
      };
    case NexusStatus.NexusTriggered:
      return {
        stroke: 'stroke-utility-error-600',
        fill: 'fill-utility-error-300',
        background: 'bg-bg-error',
      };
    case NexusStatus.ApproachingNexus:
      return {
        stroke: 'stroke-utility-warning-600',
        fill: 'fill-utility-warning-300',
        background: 'bg-bg-warning',
      };
    case NexusStatus.NoNexus:
      return {
        stroke: 'stroke-utility-gray-600',
        fill: 'fill-utility-gray-300',
        background: 'bg-bg-gray',
      };
    default:
      return {
        stroke: 'stroke-utility-gray-600',
        fill: 'fill-utility-gray-300',
        background: 'bg-bg-gray',
      };
  }
};

export const getNexusProgressColor = (status: NexusStatus) => {
  switch (status) {
    case NexusStatus.Remittance:
      return 'success';
    case NexusStatus.NexusTriggered:
      return 'error';
    case NexusStatus.ApproachingNexus:
      return 'warn';
    case NexusStatus.NoNexus:
      return 'default';
    default:
      return 'default';
  }
};

export const getTransactionStatusBadgeColor = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.LOCKED:
      return 'success';
    case TransactionStatus.UNCOMMITTED:
      return 'gray';
    case TransactionStatus.COMMITTED:
      return 'blue';
    case TransactionStatus.VOIDED:
      return 'error';
    default:
      return 'gray';
  }
};

export const getTransactionTypeOfSalesBadgeColor = (
  status: TransactionType
) => {
  switch (status) {
    case TransactionType.REFUND:
    case TransactionType.PURCHASE_REFUND:
      return 'warning';
    case TransactionType.SALE:
    case TransactionType.PURCHASE:
      return 'success';
    default:
      return 'gray';
  }
};

export const formatCurrency = (value: number): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
    currency: 'USD',
  });

  return `$${formatter.format(value)}`;
};

export const formatCurrencyWithoutDecimals = (value: number): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    // Always show 1 decimal place to avoid bad rounding (e.g., 1.5K instead of 2K)
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
    currency: 'USD',
  });

  return `$${formatter.format(value)}`;
};

export const NEXUS_STATUS_ENUM_TO_TEXT: {
  [key in NexusStatus]: string;
} = {
  [NexusStatus.NexusTriggered]: 'Exposed',
  [NexusStatus.ApproachingNexus]: 'Register Soon',
  [NexusStatus.NoNexus]: 'Not Exposed',
  [NexusStatus.Remittance]: 'Remitting',
};

export const TRANSACTION_SALE_ENUM_TO_TEXT: {
  [key in TransactionType]: string;
} = {
  [TransactionType.SALE]: 'Sale',
  [TransactionType.REFUND]: 'Refund',
  [TransactionType.PURCHASE]: 'Purchase',
  [TransactionType.PURCHASE_REFUND]: 'Purchase Refund',
};

export const SALES_TAX_STATUS_TO_TEXT: {
  [key in TransactionStatus]: string;
} = {
  [TransactionStatus.UNCOMMITTED]: 'Recorded',
  [TransactionStatus.LOCKED]: 'Locked',
  [TransactionStatus.COMMITTED]: 'To be filed',
  [TransactionStatus.VOIDED]: 'Void',
};

export const getStateName = (state: string, country?: string) => {
  if (!state) {
    return '-';
  }

  return (
    State.getStateByCodeAndCountry(state, country || Country.US)?.name || state
  );
};

export const getCountryName = (country: string) => {
  return CountryUtil.getCountryByCode(country)?.name || country;
};

/**
 * Get all US states as options for filter dropdowns
 */
export const getUSStates = (): { label: string; value: string }[] => {
  const states = State.getStatesOfCountry(Country.US);
  return (
    states?.map((state) => ({
      label: state.name,
      value: state.isoCode,
    })) || []
  );
};

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Format a tax period for display based on reporting period end date and filing frequency
 * @param reportingPeriodEndDate - ISO date string (e.g., "2024-03-31")
 * @param filingFrequency - The filing frequency enum
 * @param state - Optional state code for special handling (e.g., NY fiscal year)
 * @returns Formatted tax period string (e.g., "Jan 2024", "2024-Q1", "2024")
 */
export const formatTaxPeriod = (
  reportingPeriodEndDate: string | null | undefined,
  filingFrequency: SalesTaxFilingFrequencyEnum | null | undefined,
  state?: string
): string => {
  if (!reportingPeriodEndDate || !filingFrequency) {
    return '-';
  }

  const date = new Date(reportingPeriodEndDate);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // 1-indexed

  switch (filingFrequency) {
    case SalesTaxFilingFrequencyEnum.ANNUAL_CALENDAR_YEAR:
      return `${year}`;

    case SalesTaxFilingFrequencyEnum.FISCAL_YEAR:
      return `${MONTH_NAMES[month - 1]} ${year} (Fiscal)`;

    case SalesTaxFilingFrequencyEnum.QUARTERLY:
    case SalesTaxFilingFrequencyEnum.QUARTERLY_PREPAY_MONTHLY: {
      let quarter: number;
      // NY uses fiscal year quarters (Apr-Mar)
      if (state === 'NY') {
        quarter = month <= 3 ? 4 : Math.ceil((month - 3) / 3);
      } else {
        quarter = Math.ceil(month / 3);
      }
      return `${year}-Q${quarter}`;
    }

    case SalesTaxFilingFrequencyEnum.SEMI_ANNUALLY:
      return `${year}-H${month <= 6 ? 1 : 2}`;

    case SalesTaxFilingFrequencyEnum.MONTHLY:
    case SalesTaxFilingFrequencyEnum.MONTHLY_ACCELERATED_PREPAY_EARLY:
    default:
      return `${MONTH_NAMES[month - 1]} ${year}`;
  }
};

/**
 * Convert a tax period string to API filter parameters
 * @param taxPeriod - Tax period string (e.g., "2024-01", "2024-Q1", "2024")
 * @returns Object with reportingPeriodStart, reportingPeriodEnd, and frequencies
 */
export const taxPeriodToFilterParams = (
  taxPeriod: string
): {
  reportingPeriodStart: string;
  reportingPeriodEnd: string;
  frequencies: SalesTaxFilingFrequencyEnum[];
} => {
  // Monthly: "2024-01"
  const monthlyMatch = taxPeriod.match(/^(\d{4})-(\d{2})$/);
  if (monthlyMatch) {
    const [, yearStr, monthStr] = monthlyMatch;
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const lastDay = new Date(year, month, 0).getDate();
    return {
      reportingPeriodStart: `${yearStr}-${monthStr}-01`,
      reportingPeriodEnd: `${yearStr}-${monthStr}-${lastDay.toString().padStart(2, '0')}`,
      frequencies: [
        SalesTaxFilingFrequencyEnum.MONTHLY,
        SalesTaxFilingFrequencyEnum.MONTHLY_ACCELERATED_PREPAY_EARLY,
      ],
    };
  }

  // Quarterly: "2024-Q1"
  const quarterlyMatch = taxPeriod.match(/^(\d{4})-Q([1-4])$/);
  if (quarterlyMatch) {
    const [, yearStr, quarterStr] = quarterlyMatch;
    const year = parseInt(yearStr, 10);
    const quarter = parseInt(quarterStr, 10);
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    const lastDay = new Date(year, endMonth, 0).getDate();
    return {
      reportingPeriodStart: `${yearStr}-${startMonth.toString().padStart(2, '0')}-01`,
      reportingPeriodEnd: `${yearStr}-${endMonth.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`,
      frequencies: [
        SalesTaxFilingFrequencyEnum.QUARTERLY,
        SalesTaxFilingFrequencyEnum.QUARTERLY_PREPAY_MONTHLY,
      ],
    };
  }

  // Semi-Annual: "2024-H1" or "2024-H2"
  const semiAnnualMatch = taxPeriod.match(/^(\d{4})-H([12])$/);
  if (semiAnnualMatch) {
    const [, yearStr, halfStr] = semiAnnualMatch;
    const year = parseInt(yearStr, 10);
    if (halfStr === '1') {
      return {
        reportingPeriodStart: `${yearStr}-01-01`,
        reportingPeriodEnd: `${yearStr}-06-30`,
        frequencies: [SalesTaxFilingFrequencyEnum.SEMI_ANNUALLY],
      };
    } else {
      return {
        reportingPeriodStart: `${yearStr}-07-01`,
        reportingPeriodEnd: `${yearStr}-12-31`,
        frequencies: [SalesTaxFilingFrequencyEnum.SEMI_ANNUALLY],
      };
    }
  }

  // Annual: "2024"
  const annualMatch = taxPeriod.match(/^(\d{4})$/);
  if (annualMatch) {
    const [, yearStr] = annualMatch;
    return {
      reportingPeriodStart: `${yearStr}-01-01`,
      reportingPeriodEnd: `${yearStr}-12-31`,
      frequencies: [SalesTaxFilingFrequencyEnum.ANNUAL_CALENDAR_YEAR],
    };
  }

  // Fiscal Year: "2024-F03"
  const fiscalMatch = taxPeriod.match(/^(\d{4})-F(\d{2})$/);
  if (fiscalMatch) {
    const [, yearStr, monthStr] = fiscalMatch;
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const lastDay = new Date(year, month, 0).getDate();
    return {
      reportingPeriodStart: `${yearStr}-${monthStr}-01`,
      reportingPeriodEnd: `${yearStr}-${monthStr}-${lastDay.toString().padStart(2, '0')}`,
      frequencies: [SalesTaxFilingFrequencyEnum.FISCAL_YEAR],
    };
  }

  // Fallback - treat as monthly
  throw new Error(`Invalid tax period format: ${taxPeriod}`);
};

/**
 * Generate tax period options for the last 3 years
 * Options include monthly, quarterly, and annual periods
 * @returns Array of options for the tax period filter dropdown
 */
export const generateTaxPeriodOptions = (): {
  label: string;
  value: string;
}[] => {
  const options: { label: string; value: string }[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Generate options for last 3 years + current year
  for (let year = currentYear; year >= currentYear - 3; year--) {
    // Annual
    if (year < currentYear || currentMonth === 12) {
      options.push({ label: `${year} (Annual)`, value: `${year}` });
    }

    // Quarterly (Q4 to Q1)
    for (let q = 4; q >= 1; q--) {
      const quarterEndMonth = q * 3;
      if (year < currentYear || currentMonth >= quarterEndMonth) {
        options.push({ label: `${year}-Q${q}`, value: `${year}-Q${q}` });
      }
    }

    // Monthly (Dec to Jan)
    for (let m = 12; m >= 1; m--) {
      if (year < currentYear || m <= currentMonth) {
        const monthStr = m.toString().padStart(2, '0');
        options.push({
          label: `${monthNames[m - 1]} ${year}`,
          value: `${year}-${monthStr}`,
        });
      }
    }
  }

  return options;
};

